import { useEffect } from 'react';
import Hero from './components/Hero';
import About from './components/About';
import HowItWorks from './components/HowItWorks';
import WhyChoose from './components/WhyChoose';
import RevealChart from '../simulation/components/RevealChart';
import Pricing from './components/Pricing';
import Contact from './components/Contact';
import FAQ from './components/FAQ';
import Footer from './components/Footer';
import ProblemSection from './components/ProblemSection';
import BenefitsSection from './components/BenefitsSection';

const faqSchemaItems = [
  {
    question: "Who is OverUnder Roofing for?",
    answer: "This service is a great fit if: You want multiple bids but don't trust them or know how to compare them properly. You're concerned about overpaying or getting poor-quality work. You don't have the time or expertise to vet contractors and manage the process yourself.",
  },
  {
    question: "What's included in the $500 fee?",
    answer: "The $500 flat fee covers the entire bidding process: project intake, contractor outreach, bid collection and coordination, detailed bid analysis, quality comparisons, and our expert recommendation. You also get post-job support to ensure everything goes smoothly.",
  },
  {
    question: "How do you vet contractors?",
    answer: "Every contractor in our network is licensed, insured, and bonded in North Carolina. We personally verify their credentials, check references, review past work, and assess their reputation in the community.",
  },
  {
    question: "Can I use my own contractor recommendation?",
    answer: "Absolutely! If you have a contractor you already trust or one that's been recommended by a friend, we'll coordinate their bid alongside the others. This gives you a true apples-to-apples comparison. There's no extra charge for including your own pick.",
  },
  {
    question: "What if I don't like any of the bids?",
    answer: "You're never obligated to move forward with any bid. If none of the options meet your expectations, we can discuss next steps — whether that's adjusting project scope, waiting for better timing, or exploring additional contractors.",
  },
  {
    question: "Do you work with insurance claims?",
    answer: "Yes, we can help coordinate bids for insurance-related roof repairs or replacements. However, we don't handle insurance negotiations directly. We recommend working with a public adjuster if you're dealing with a complex claim.",
  },
  {
    question: "What areas do you serve?",
    answer: "We serve Wake County and the Triangle area, including Raleigh, Cary, Apex, Wake Forest, Holly Springs, Fuquay-Varina, Garner, and surrounding communities in North Carolina.",
  },
  {
    question: "How long does the bidding process take?",
    answer: "From intake to final bid presentation, the process typically takes 1–2 weeks. This includes time for us to reach out to contractors, schedule site visits, collect detailed bids, and prepare our analysis.",
  },
  {
    question: "What's the success fee structure?",
    answer: "After the $500 flat fee, we charge a success fee only if you move forward with a contractor. This fee is the lesser of 5% of the total job cost OR 25% of the savings we help you achieve compared to the highest bid.",
  },
  {
    question: "Are you licensed contractors?",
    answer: "No, we are not licensed contractors. We're independent roofing advocates and consultants. We don't perform roofing work ourselves — we coordinate bids from licensed, insured contractors and provide unbiased guidance.",
  },
  {
    question: "What happens after I choose a contractor?",
    answer: "Once you select a contractor, we'll help facilitate the contract signing and project kickoff. We remain available throughout the project to answer questions, address concerns, and ensure everything stays on track.",
  },
  {
    question: "Is hiring a roofing consultant worth it?",
    answer: "For most homeowners, yes. Roofing quotes can vary by thousands of dollars with no clear explanation. A consultant helps you compare bids properly, avoid overpaying, and reduce the risk of poor workmanship — often saving more than the consulting fee.",
  },
  {
    question: "Why do roofing quotes vary so much?",
    answer: "Roofing bids often differ due to variations in materials, scope, labor assumptions, and hidden exclusions. Two quotes may look similar in price but include very different levels of quality and protection.",
  },
  {
    question: "Why isn't getting 2–3 bids enough?",
    answer: "Getting multiple bids is a good start — but without expert analysis, you're still guessing. Most homeowners don't have the experience to evaluate scope differences, material quality, or pricing strategies.",
  },
  {
    question: "How much can you actually save using OverUnder Roofing?",
    answer: "Savings vary by project, but many homeowners avoid overpaying by thousands of dollars or secure higher-quality materials for the same price. Our goal is to help you find the best value — not just the lowest price.",
  },
  {
    question: "Who is this service NOT ideal for?",
    answer: "This may not be the best fit if: You already have a contractor you fully trust and are happy with their quote. You prefer to handle the entire bidding and hiring process yourself.",
  },
];

export default function HomePage() {
  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://overunderroofing.com';
    const logoUrl = 'https://public.readdy.ai/ai/img_res/82f8a00e-bc28-4af6-bf3f-740e76e3ab6b.png';

    // --- FAQPage Schema ---
    const existingFaq = document.getElementById('faq-schema');
    if (existingFaq) existingFaq.remove();
    const faqScript = document.createElement('script');
    faqScript.id = 'faq-schema';
    faqScript.type = 'application/ld+json';
    faqScript.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqSchemaItems.map(item => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: { '@type': 'Answer', text: item.answer },
      })),
    });
    document.head.appendChild(faqScript);

    // --- LocalBusiness Schema ---
    const existingLb = document.getElementById('localbusiness-schema');
    if (existingLb) existingLb.remove();
    const lbScript = document.createElement('script');
    lbScript.id = 'localbusiness-schema';
    lbScript.type = 'application/ld+json';
    lbScript.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      name: 'OverUnder Roofing',
      legalName: 'C&K Baker Ventures LLC',
      description:
        'Independent roofing advocate helping Wake County homeowners get transparent, competitive bids from vetted, licensed contractors in the Raleigh, Cary, Apex, and Triangle area.',
      url: siteUrl,
      telephone: '+19193497701',
      email: 'overunderroofing@gmail.com',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '2474 Walnut Street #214',
        addressLocality: 'Cary',
        addressRegion: 'NC',
        postalCode: '27518',
        addressCountry: 'US',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 35.7915,
        longitude: -78.7811,
      },
      areaServed: [
        { '@type': 'City', name: 'Raleigh', sameAs: 'https://www.wikidata.org/wiki/Q179230' },
        { '@type': 'City', name: 'Cary', sameAs: 'https://www.wikidata.org/wiki/Q928683' },
        { '@type': 'City', name: 'Apex', sameAs: 'https://www.wikidata.org/wiki/Q1069047' },
        { '@type': 'City', name: 'Wake Forest' },
        { '@type': 'City', name: 'Holly Springs' },
        { '@type': 'City', name: 'Fuquay-Varina' },
        { '@type': 'City', name: 'Garner' },
      ],
      serviceType: 'Roofing Bid Consultation and Advocacy',
      priceRange: '$500 flat fee',
      currenciesAccepted: 'USD',
      paymentAccepted: 'Credit Card, Check',
      openingHoursSpecification: [
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
          opens: '08:00',
          closes: '18:00',
        },
      ],
      sameAs: [
        'https://www.facebook.com/overunderroofing',
        'https://www.linkedin.com/company/overunderroofing',
      ],
    });
    document.head.appendChild(lbScript);

    // --- Organization Schema ---
    const existingOrg = document.getElementById('organization-schema');
    if (existingOrg) existingOrg.remove();
    const orgScript = document.createElement('script');
    orgScript.id = 'organization-schema';
    orgScript.type = 'application/ld+json';
    orgScript.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'OverUnder Roofing',
      legalName: 'C&K Baker Ventures LLC',
      url: siteUrl,
      logo: logoUrl,
      telephone: '+19193497701',
      email: 'overunderroofing@gmail.com',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '2474 Walnut Street #214',
        addressLocality: 'Cary',
        addressRegion: 'NC',
        postalCode: '27518',
        addressCountry: 'US',
      },
      foundingLocation: {
        '@type': 'Place',
        name: 'Wake County, North Carolina',
      },
      knowsAbout: [
        'Roofing bid analysis',
        'Contractor vetting',
        'Homeowner advocacy',
        'Wake County roofing market',
        'Roofing cost transparency',
      ],
    });
    document.head.appendChild(orgScript);

    // --- WebSite Schema ---
    const existingWs = document.getElementById('website-schema');
    if (existingWs) existingWs.remove();
    const wsScript = document.createElement('script');
    wsScript.id = 'website-schema';
    wsScript.type = 'application/ld+json';
    wsScript.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'OverUnder Roofing',
      url: siteUrl,
      description:
        'Independent roofing advocate for Wake County and Triangle area homeowners. Get transparent, competitive bids without commission conflicts.',
      publisher: { '@type': 'Organization', name: 'OverUnder Roofing' },
    });
    document.head.appendChild(wsScript);

    return () => {
      ['faq-schema', 'localbusiness-schema', 'organization-schema', 'website-schema'].forEach(id => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* React 19 document metadata */}
      <title>Cary NC Roofing Consultant – Transparent Bids | OverUnder Roofing</title>
      <meta name="description" content="Wake County's independent roofing advocate. Expert bid analysis, vetted contractors, transparent pricing in Raleigh, Cary, Apex & Triangle NC. $500 flat fee, no commission conflicts." />
      <link rel="canonical" href="https://overunderroofing.com/" />

      <Hero />
      <main>
        <ProblemSection />
        <HowItWorks />
        <BenefitsSection />

        {/* What Homeowners See vs. What We See */}
        <section id="value-difference" className="bg-gradient-to-b from-slate-50 to-white py-16 sm:py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-100 rounded-full px-4 py-1.5 mb-5">
                <i className="ri-bar-chart-grouped-line text-emerald-600 text-sm" />
                <span className="text-xs font-semibold text-emerald-700">Interactive Visualization</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mb-4 leading-tight tracking-tight">
                What Homeowners See
                <span className="block text-emerald-700">vs. What We See</span>
              </h2>
              <p className="text-slate-500 text-base sm:text-lg max-w-xl mx-auto leading-relaxed">
                Most people get 2–3 random bids and pick based on price. Click to see what they miss.
              </p>
            </div>
            <RevealChart />
          </div>
        </section>

        <WhyChoose />
        <About />
        <Pricing />
        <Contact />
        <FAQ />
      </main>
      <Footer />
    </div>
  );
}
